/**
 * Authentication & Authorization Helpers
 * 
 * Centralized auth logic for role-based access control.
 * Designed to scale with future roles (brand, admin, team members, etc.)
 */

import { GoogleAuthProvider, signInWithPopup, User } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './firebase';
import { useAuth } from '../context/AuthContext';
import { ensureUserDoc } from './firestore/users';

/**
 * User roles in the system
 * 
 * Future roles can be added here:
 * - 'team_member' - For multi-user brand accounts
 * - 'viewer' - Read-only access
 * - 'moderator' - Content moderation access
 */
export type UserRole = 'influencer' | 'creator' | 'brand' | 'admin';

/**
 * Get current authenticated user
 * 
 * @returns User object or null if not authenticated
 */
export function getCurrentUser() {
  // This would be used in non-React contexts
  // For React components, use useAuth() hook instead
  throw new Error('Use useAuth() hook in React components');
}

/**
 * React hook for role-based access control
 * 
 * Usage:
 *   const { isInfluencer, canAccess } = useRequireRole('influencer');
 *   if (!canAccess) return <Redirect />;
 * 
 * @param requiredRole - Role required to access resource
 * @returns Object with role checks and access status
 */
export function useRequireRole(requiredRole: UserRole) {
  const { user, profile, loading } = useAuth();
  
  const userRole = profile?.userType || profile?.role as UserRole | undefined;
  const isAuthenticated = !!user;
  const hasRequiredRole = userRole === requiredRole;
  const canAccess = isAuthenticated && hasRequiredRole && !loading;
  
  return {
    isAuthenticated,
    hasRequiredRole,
    canAccess,
    userRole,
    loading,
  };
}

/**
 * React hook for multi-role access (OR logic)
 * 
 * Usage:
 *   const { canAccess } = useRequireAnyRole(['influencer', 'admin']);
 * 
 * @param allowedRoles - Array of roles that can access
 * @returns Object with access status
 */
export function useRequireAnyRole(allowedRoles: UserRole[]) {
  const { user, profile, loading } = useAuth();
  
  const userRole = profile?.userType || profile?.role as UserRole | undefined;
  const isAuthenticated = !!user;
  const hasRequiredRole = userRole ? allowedRoles.includes(userRole) : false;
  const canAccess = isAuthenticated && hasRequiredRole && !loading;
  
  return {
    isAuthenticated,
    hasRequiredRole,
    canAccess,
    userRole,
    loading,
  };
}

/**
 * Check if current user owns a resource
 * 
 * @param resourceUserId - User ID that owns the resource
 * @returns true if current user owns the resource
 */
export function useIsOwner(resourceUserId: string) {
  const { user } = useAuth();
  return user?.uid === resourceUserId;
}

/**
 * Sign in with Google using a popup
 * 
 * @returns Firebase User object
 */
export async function signInWithGoogle() {
  const provider = new GoogleAuthProvider();
  try {
    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (error: any) {
    if (error.code === 'auth/account-exists-with-different-credential') {
      throw new Error('An account already exists with this email. Please sign in with email and password first.');
    }
    if (error.code === 'auth/operation-not-allowed') {
      throw new Error('Google Sign-In is not enabled. Please enable it in Firebase Console under Authentication > Sign-in method.');
    }
    throw error;
  }
}

/**
 * Ensures a user document exists in Firestore from a Firebase Auth user
 * Idempotent: merges auth user fields without overwriting existing onboarding/profile data
 * 
 * @param user - Firebase Auth User object
 * @returns Firestore document snapshot
 */
export async function ensureUserDocFromAuthUser(user: User) {
  if (!user || !user.uid) {
    throw new Error('ensureUserDocFromAuthUser: user with uid is required');
  }

  // Check if doc exists BEFORE calling ensureUserDoc (which creates it if missing)
  const userRef = doc(db, 'users', user.uid);
  const existingSnap = await getDoc(userRef);
  const existingData = existingSnap.exists() ? existingSnap.data() : null;
  const docExists = existingSnap.exists();
  const onboardingCompleted =
    existingData?.onboardingCompleted === true ||
    existingData?.onboardingStatus === 'completed';
  const hasDisplayName = !!(existingData?.displayName || existingData?.name);

  // First, ensure the basic user doc exists (handles role, status, etc.)
  await ensureUserDoc(user.uid, user.email || null);

  // Build update payload with safe fields from auth user
  // Only set fields that come from the auth provider, don't overwrite onboarding/profile data
  const updatePayload: Record<string, any> = {
    email: user.email ? user.email.toLowerCase() : null,
    updatedAt: serverTimestamp(),
  };

  // Set displayName from auth user if available (but don't overwrite if doc already has one)
  if (user.displayName && (onboardingCompleted || hasDisplayName)) {
    updatePayload.displayName = user.displayName;
  }

  // NOTE: photoURL is NOT set from Google OAuth
  // Profile pictures must be manually uploaded during onboarding Step 1
  // and can be changed later in profile editing

  // Only set createdAt if the doc doesn't exist yet
  if (!docExists) {
    updatePayload.createdAt = serverTimestamp();
  }

  // Merge the update (won't overwrite existing onboarding, profile, or other fields)
  await setDoc(userRef, updatePayload, { merge: true });

  return getDoc(userRef);
}

/**
 * Permission checks for future features
 * 
 * These can be extended for:
 * - Team accounts (check if user is team member)
 * - Paid tiers (check subscription status)
 * - Feature flags (check if feature is enabled for user)
 */

